import React from 'react'
import { format } from 'date-fns'
import { EditionV4DTO } from '@west-australian-newspapers/publication-types'
import {
    AllEvents,
    createEditionSelectItemEvent,
    Product,
    scaleImage,
    ValidCategories,
    WebLink,
} from '@news-mono/web-common'
import { NightlyButton } from '../../../buttons/NightlyButton/NightlyButton'
import {
    StyledNightlyEditionContainer,
    StyledNightlyEditionHeading,
    StyledNightlyEditionTextContainer,
    StyledNightlyEditionSubHeading,
    StyledNightlyEditionTeaser,
} from '../SpecificEdition/NightlySpecificEdition.styled'
import {
    HeaderContainer,
    StyledDivider,
    StyledNightlyEditionButtonContainerLeft,
    StyledNightlyEditionContentHome,
    StyledNightlyEditionPictureRight,
    StyledNightlyEditionTextContainerLeft,
} from './SpecialEditionHome.styled'
import { HeaderPrefix } from '../SpecialEdition/SpecialEdition.styled'
import { breakpointMax, breakpoint } from '../../../__styling'


interface SpecialEditionHomeProps {
    edition: EditionV4DTO
    publicationTitle: string
    lazyloadImages?: boolean
    onEvent: (event: AllEvents) => void
    isTravelEdition?: boolean
    heading?: string
    showFirstDivider?: boolean
}

export const SpecialEditionHome = ({
    edition,
    publicationTitle,
    lazyloadImages,
    onEvent,
    isTravelEdition = false,
    heading,
    showFirstDivider = true,
}: SpecialEditionHomeProps) => {
    const mainImage = edition.mainImages && {
        img: scaleImage(edition.mainImages[0].src, 600, false),
        alt: edition.mainImages[0].altText ?? edition.mainImages[0].captionText,
    }

    // The title for "The Nightly On" editions is in the format "title_DD-MM-YYYY", this
    // removes the date part and normalizes the title to be capitalized.
    // Travel edition uses a fixed heading, so we use that if provided.
    const normalizedEditionTitle = heading
        ? heading
        : edition.title
        ? edition.title.split('_')[0].charAt(0).toUpperCase() +
          edition.title.split('_')[0].slice(1).toLowerCase() +
          ' Edition'
        : ''

    const handleImageClick = () => {
        onEvent &&
            onEvent(
                createEditionSelectItemEvent({
                    edition,
                    collectionName: 'TheNightlyOn_ImageButton',
                    product: Product.TheNightly,
                    itemDetails: {
                        category: 'thenightly_on_cta',

                        linkText: 'Image Clicked',
                        linkUrl: edition.primaryTopic
                            ? `${edition.primaryTopic}`
                            : '/on',
                    },
                }),
            )
    }

    const handleButtonClick = ({
        collectionName,
        category,
        linkText,
        linkUrl,
    }: {
        collectionName: string
        category: ValidCategories
        linkText: string
        linkUrl: string
    }) => {
        onEvent &&
            onEvent(
                createEditionSelectItemEvent({
                    edition,
                    collectionName,
                    product: Product.TheNightly,
                    itemDetails: {
                        category,
                        linkText,
                        linkUrl,
                    },
                }),
            )
    }

    const buttonProps = {
        variant: 'default',
        color: 'primary',
        fill: 'filled',
        layout: {
            shouldGrow: true,
            alignSelf: 'stretch',
        },
    } as const

    const primaryButtonProps = {
        ...buttonProps,
        text: isTravelEdition
            ? 'Read the Latest Edition'
            : normalizedEditionTitle,
        action: {
            type: 'link',
            to: edition.editionWebUrl,
            onClick: () =>
                handleButtonClick({
                    collectionName: isTravelEdition
                        ? 'TheNightlyTravel_ReadEditionButton'
                        : 'TheNightlyOn_ReadEditionButton',
                    category: isTravelEdition
                        ? 'thenightly_travel_cta'
                        : 'thenightly_on_cta',
                    linkText: isTravelEdition
                        ? 'Read the Latest Edition'
                        : normalizedEditionTitle,
                    linkUrl: edition.editionWebUrl,
                }),
        },
    } as const

    const secondaryButtonProps = {
        ...buttonProps,
        fill: 'outline',
        text: isTravelEdition ? 'All Editions' : 'More Articles',
        action: {
            type: 'link',
            to: isTravelEdition
                ? '/lifestyle/travel/editions'
                : edition.primaryTopic
                ? `${edition.primaryTopic}`
                : '/on',
            onClick: () =>
                handleButtonClick({
                    collectionName: isTravelEdition
                        ? 'TheNightlyTravel_MoreArticlesButton'
                        : 'TheNightlyOn_MoreArticlesButton',
                    category: isTravelEdition
                        ? 'thenightly_travel_cta'
                        : 'thenightly_on_cta',
                    linkText: isTravelEdition
                        ? 'All Editions'
                        : 'More Articles',
                    linkUrl: isTravelEdition
                        ? '/lifestyle/travel/editions'
                        : edition.primaryTopic
                        ? `${edition.primaryTopic}`
                        : '/on',
                }),
        },
    } as const

    const subHeading = isTravelEdition
        ? format(new Date(edition.publicationDate), 'd MMMM yyyy')
        : 'In this Edition'

    return (
        <>
            {showFirstDivider && <StyledDivider />}
            <StyledNightlyEditionContainer style={{ paddingBottom: 0 }}>
                <StyledNightlyEditionContentHome>
                    <HeaderContainer>
                        {!isTravelEdition && (
                            <HeaderPrefix>{publicationTitle}</HeaderPrefix>
                        )}
                        <StyledNightlyEditionHeading>
                            {isTravelEdition
                                ? normalizedEditionTitle.toUpperCase()
                                : normalizedEditionTitle}
                        </StyledNightlyEditionHeading>
                        {isTravelEdition && (
                            <HeaderPrefix>{publicationTitle}</HeaderPrefix>
                        )}
                    </HeaderContainer>
                    <StyledNightlyEditionTextContainerLeft>
                        <StyledNightlyEditionSubHeading>
                            {subHeading}
                        </StyledNightlyEditionSubHeading>
                        <StyledNightlyEditionTeaser>
                            {edition.teaser}
                        </StyledNightlyEditionTeaser>
                    </StyledNightlyEditionTextContainerLeft>
                    <StyledNightlyEditionButtonContainerLeft>
                        <NightlyButton {...primaryButtonProps} />
                        <NightlyButton {...secondaryButtonProps} />
                    </StyledNightlyEditionButtonContainerLeft>
                    <StyledNightlyEditionPictureRight>
                        <WebLink
                            to={edition.editionWebUrl}
                            onClick={handleImageClick}
                        >
                            {mainImage && (
                                <picture>
                                    <source
                                        srcSet={mainImage.img}
                                        media={breakpointMax('sm', false)}
                                    />
                                    <source
                                        srcSet={mainImage.img}
                                        media={breakpoint('sm', false)}
                                    />
                                    <img
                                        src={mainImage.img}
                                        onError={({ currentTarget }) => {
                                            currentTarget.onerror = null
                                        }}
                                        alt={mainImage.alt}
                                        loading={
                                            lazyloadImages ? 'lazy' : undefined
                                        }
                                    />
                                </picture>
                            )}
                        </WebLink>
                    </StyledNightlyEditionPictureRight>
                </StyledNightlyEditionContentHome>
            </StyledNightlyEditionContainer>
        </>
    )
}
